import { useSession } from '@side/next-auth/react';
import { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { useAgentDslFilters } from '@/common/hooks/useAgentDslFilters';
import { useSubmitAsTeamMembers } from '@/common/hooks/useSubmitAsTeamMembers';
import { doesAgentHaveRequiredFields } from '@/common/utils/agents';
import { buildSearchFilterQuery } from '@/common/utils/buildSearchFilterQuery';
import { buildUserIdsCondition } from '@/common/utils/identityUserIdsCondition';
import {
  type AgentProfileSummaryFields,
  type AgentProfileTeamMemberInput,
  mapTeamMemberToAgentProfile,
} from '$agent-profiles';
import { type IdentityRequest, useIdentity } from '$api-clients/hooks';
import { useGlobalAgentSearchEnabled } from './useGlobalAgentSearchEnabled';

type TeamMember = NonNullable<
  IdentityRequest<'/team-members'>['Data']['data']
>[number];

interface UseAgentAutocompleteProps {
  search: string;
  /** Clauses for the identity `q` param. Build with `useAgentDslFilters`. */
  filters?: string[];
  userIdsForDefaultQuery?: string[];
}

/**
 * Scopes a `q` query to the given users' team memberships. Caller-supplied ids
 * are a mix of identity UUIDs and legacy ids, so both are matched.
 */
export function buildUserScopedAgentQuery(
  userIds: string[],
  filters: string[],
): string {
  return [buildUserIdsCondition(userIds), ...filters]
    .filter(Boolean)
    .join(' & ');
}

function buildAgentList(
  data: (TeamMember | AgentProfileTeamMemberInput)[] | undefined,
): AgentProfileSummaryFields[] {
  if (!data) return [];

  return data
    .reduce<AgentProfileSummaryFields[]>((acc, member) => {
      const agent = mapTeamMemberToAgentProfile(member);
      if (doesAgentHaveRequiredFields(agent)) {
        acc.push(agent);
      }

      return acc;
    }, [])
    .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
}

export function useDefaultPrimaryAgent() {
  const { data: session } = useSession();
  const currentUserId = session?.user?.id ?? '';
  const filters = useAgentDslFilters();

  const { isLoading, data } = useIdentity(
    '/team-members',
    currentUserId
      ? {
          params: {
            query: {
              q: buildUserScopedAgentQuery([currentUserId], filters),
            },
          },
        }
      : null,
  );

  const members = data?.data;

  const allAgents = useMemo(() => buildAgentList(members), [members]);

  const defaultAgent = allAgents.length === 1 ? allAgents[0] : null;

  return {
    loading: isLoading,
    defaultAgent,
  };
}

export function useAgentAutocompleteOptions({
  search,
  filters,
  userIdsForDefaultQuery = [],
}: UseAgentAutocompleteProps) {
  const { data: session } = useSession();
  const currentUserId = session?.user?.id ?? '';

  const defaultFilters = useAgentDslFilters();
  const agentFilters = filters ?? defaultFilters;

  const [debouncedSearchValue, setDebouncedSearchValue] = useState(search);
  useDebounce(() => setDebouncedSearchValue(search), 300, [
    search,
    setDebouncedSearchValue,
  ]);

  // If no search term is entered, we show a list consisting of the current
  // user's team members and the transaction owner's team members.
  const defaultQuery = useMemo(
    () =>
      buildUserScopedAgentQuery(
        userIdsForDefaultQuery.concat(currentUserId),
        agentFilters,
      ),
    [userIdsForDefaultQuery, currentUserId, agentFilters],
  );

  const { isLoading: defaultOptionsLoading, data: defaultOptionsData } =
    useIdentity(
      '/team-members',
      currentUserId && !debouncedSearchValue
        ? {
            params: {
              query: {
                q: defaultQuery,
              },
            },
          }
        : null,
      { keepPreviousData: true },
    );

  const defaultOptions = useMemo(
    () => buildAgentList(defaultOptionsData?.data),
    [defaultOptionsData?.data],
  );

  const globalSearchEnabled = useGlobalAgentSearchEnabled();

  // If a search term is entered, we show just the submit as team members that
  // match the search.
  const { data: submitAsTeamMembers, loading: submitAsOptionsLoading } =
    useSubmitAsTeamMembers({
      search: debouncedSearchValue,
      filters: agentFilters,
      skip: !debouncedSearchValue || globalSearchEnabled,
    });

  const submitAsOptions: AgentProfileSummaryFields[] = useMemo(() => {
    return (submitAsTeamMembers ?? []).filter(doesAgentHaveRequiredFields);
  }, [submitAsTeamMembers]);

  // Admins with global interaction access search across all orgs instead of
  // being limited to their submit-as grants (e.g. reassigning transactions for
  // offboarded agents in other orgs).
  const { isLoading: globalOptionsLoading, data: globalOptionsData } =
    useIdentity(
      '/team-members',
      globalSearchEnabled && debouncedSearchValue
        ? {
            params: {
              query: {
                q: buildSearchFilterQuery(debouncedSearchValue, agentFilters),
                limit: 25,
              },
            },
          }
        : null,
      { keepPreviousData: true },
    );

  const globalOptions = useMemo(
    () => buildAgentList(globalOptionsData?.data),
    [globalOptionsData?.data],
  );

  const searchOptions = globalSearchEnabled ? globalOptions : submitAsOptions;
  const searchOptionsLoading = globalSearchEnabled
    ? globalOptionsLoading
    : submitAsOptionsLoading;

  return {
    agents: debouncedSearchValue ? searchOptions : defaultOptions,
    loading: debouncedSearchValue
      ? searchOptionsLoading
      : defaultOptionsLoading,
  };
}
